/** Presentation helpers for Auto Sourcing Results summary (not persisted). */

export const MAX_SCAN_DURATION_MINUTES = 72 * 60; // 72 continuous hours

export function compactNumber(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  const abs = Math.abs(v);
  if (abs >= 1e6) {
    const s = (v / 1e6).toFixed(abs >= 1e7 ? 1 : 2).replace(/\.?0+$/, "");
    return `${s}M`;
  }
  if (abs >= 1000) {
    const s = (v / 1000).toFixed(1).replace(/\.0$/, "");
    return `${s}K`;
  }
  return String(Math.round(v));
}

/** Elapsed duration without leading/zero units (27s, 7m 27s, 2h 7m 27s, 1d 2h). */
export function formatDuration(totalSeconds) {
  let s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const d = Math.floor(s / 86400);
  s %= 86400;
  const h = Math.floor(s / 3600);
  s %= 3600;
  const m = Math.floor(s / 60);
  s %= 60;
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  // Seconds only when non-zero, or when they are the sole unit (incl. 0s).
  if (s || parts.length === 0) parts.push(`${s}s`);
  return parts.join(" ");
}

export function elapsedSeconds(run) {
  const start = run?.started_at || run?.created_at;
  if (!start) return 0;
  const t0 = new Date(start).getTime();
  if (!Number.isFinite(t0)) return 0;
  const terminal = ["completed", "partial", "failed", "cancelled"].includes(run?.status);
  const end = terminal
    ? new Date(run?.completed_at || run?.updated_at || Date.now()).getTime()
    : Date.now();
  if (!Number.isFinite(end) || end < t0) return 0;
  return Math.round((end - t0) / 1000);
}

export function formatStartedAt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Split minutes into { amount, unit } for the duration selector. */
export function minutesToDurationParts(minutes) {
  const m = Math.max(1, Math.floor(Number(minutes) || 30));
  if (m % (24 * 60) === 0) return { amount: m / (24 * 60), unit: "days" };
  if (m % 60 === 0 && m >= 60) return { amount: m / 60, unit: "hours" };
  return { amount: m, unit: "minutes" };
}

export function durationPartsToMinutes(amount, unit) {
  const n = Math.max(1, Math.floor(Number(amount) || 1));
  if (unit === "days") return n * 24 * 60;
  if (unit === "hours") return n * 60;
  return n;
}

const EARLY_STOP_LABELS = {
  budget_exhausted: "Stopped early — Provider budget exhausted",
  aggregate_budget_exhausted: "Stopped early — Provider budget exhausted",
  partition_exhausted: "Stopped early — Provider budget exhausted",
  categories_exhausted: "Completed early — All selected categories exhausted",
  all_selected_categories_exhausted: "Completed early — All selected categories exhausted",
  sources_exhausted: "Completed early — All selected sources exhausted",
  // Stage K — honest terminal reasons for a genuinely continuous scan.
  custom_limit_reached: "Completed — Maximum products to discover reached",
  work_exhausted: "Completed early — No further eligible products",
  pagination_unproven: "Completed early — Listing pagination exhausted",
  // Stage L acceptance — a crashing job must not look like a healthy scan.
  internal_error: "Stopped — Internal error",
  repeated_failure: "Stopped — Repeated failures",
  pause_expired: "Stopped — Pause expired",
  cancelled: "Cancelled",
  user_cancelled: "Cancelled",
  manual_stop: "Cancelled",
  safety_circuit_breaker: "Stopped early — Safety circuit breaker",
  access_blocked: "Stopped early — Access blocked",
  parser_failure: "Stopped early — Parser failure",
  // Stage R: distinguish a page we could not render from one we could read but
  // could not parse, and from a listing that legitimately had no products.
  js_render_required: "Stopped early — Listing requires JavaScript rendering",
  render_unavailable: "Stopped early — Rendering provider unavailable",
  // Stage S: provider-side failures must be named, not shown as a parse defect.
  provider_gateway_failure: "Stopped early — Provider gateway failure",
  structured_unavailable: "Stopped early — Structured data unavailable",
  empty_category: "Completed — Category has no products",
  homepage_redirect: "Stopped early — Retailer redirected to its home page",
  discovery_unavailable: "Stopped early — Discovery unavailable",
  provider_timeout: "Stopped early — Provider timeout",
  no_products_found: "Completed — No products found",
  all_products_rejected: "Completed — All products rejected",
  completed_with_partial_failures: "Completed with partial failures",
  deadline_reached: "Completed — Duration reached",
};

export function statusPresentation(status, stopReason) {
  const s = String(status || "queued").toLowerCase();
  const stop = String(stopReason || "").toLowerCase();
  if (s === "running" || s === "collecting" || s === "matching" || s === "verifying") {
    return { label: "Running", tone: "green" };
  }
  if (s === "queued") return { label: "Queued", tone: "neutral" };
  if (s === "paused") return { label: "Paused", tone: "neutral" };
  if (
    stop === "resume_required"
    || stop === "lease_expired"
    || stop === "worker_interrupted"
  ) {
    return { label: "Resume required", tone: "amber" };
  }
  if (EARLY_STOP_LABELS[stop]) {
    const tone =
      stop.includes("budget") || stop.includes("partition") || stop.includes("safety")
        ? "amber"
        : stop.includes("fail") || stop.includes("blocked") || stop.includes("unavailable")
          ? "red"
          : stop.includes("cancel")
            ? "gray"
            : "green";
    return { label: EARLY_STOP_LABELS[stop], tone };
  }
  if (s === "budget_exhausted") {
    return { label: EARLY_STOP_LABELS.budget_exhausted, tone: "amber" };
  }
  if (s === "cancelled") return { label: "Cancelled", tone: "gray" };
  if (s === "failed") return { label: "Failed", tone: "red" };
  if (s === "completed" || s === "partial") {
    if (stop && stop !== "completed") {
      return {
        label: `Completed early — ${stop.replace(/_/g, " ")}`,
        tone: "amber",
      };
    }
    return { label: "Completed", tone: "green" };
  }
  return { label: String(status || "Queued"), tone: "neutral" };
}

/** Authorized scan window in seconds (from run / job limits). */
export function authorizedDurationSeconds(run) {
  const explicit = Number(run?.authorized_duration_seconds);
  if (Number.isFinite(explicit) && explicit > 0) return Math.floor(explicit);
  const mins =
    run?.max_duration_minutes
    ?? run?.effective_limits?.max_duration_minutes
    ?? run?.progress?.max_duration_minutes
    ?? null;
  if (mins == null || !Number.isFinite(Number(mins))) return null;
  return Math.max(0, Math.floor(Number(mins) * 60));
}

/**
 * Active scanning time consumed so far, in seconds.
 *
 * Prefers the server's authoritative `duration_consumed_seconds`, and adds the
 * time elapsed since the server-side attempt started (`active_started_at`) so
 * the timer and progress bar advance between polls instead of jumping at the end.
 *
 * A `null` server value means "not finalized yet" — never zero.
 */
function activeStartMs(run) {
  // `active_started_at` is the authoritative scan start; `started_at` is now
  // also the active start from the API. Never `created_at` — that is queue time.
  const raw = run?.active_started_at || run?.started_at;
  if (!raw) return NaN;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : NaN;
}

export function consumedDurationSeconds(run, now = Date.now()) {
  const status = String(run?.status || "").toLowerCase();
  const paused = status === "paused";
  const terminal = ["completed", "partial", "failed", "cancelled"].includes(status);
  const raw = run?.duration_consumed_seconds;
  const server = raw === null || raw === undefined || raw === "" ? null : Number(raw);
  const baseline = Number.isFinite(server) && server >= 0 ? Math.floor(server) : null;

  // Settled attempt: the server's committed number is authoritative.
  if (terminal || paused) {
    if (baseline != null) return baseline;
    const startedMs = activeStartMs(run);
    return Number.isFinite(startedMs) ? Math.max(0, Math.floor((now - startedMs) / 1000)) : 0;
  }

  // Queued means "waiting for an available worker" — the scan has not begun, so
  // nothing of the authorized window is consumed. Counting queue wait as scan
  // time made a 17m11s wait display as 57% of a 30-minute window.
  if (status === "queued") return baseline != null ? baseline : 0;

  // Live attempt: add the wall time since the SERVER's attempt start so the bar
  // advances between polls. `active_started_at` is the only authoritative
  // attempt clock; `started_at` is used below only when no baseline exists.
  const attemptMs = _parseMs(run?.active_started_at);
  if (baseline != null) {
    if (Number.isFinite(attemptMs)) {
      return baseline + Math.max(0, Math.floor((now - attemptMs) / 1000));
    }
    return baseline;
  }
  if (Number.isFinite(attemptMs)) {
    return Math.max(0, Math.floor((now - attemptMs) / 1000));
  }
  // No committed or attempt evidence: count from the active scan start only.
  // Never from `created_at` — that would re-introduce queue wait as scan time.
  const scanStartMs = activeStartMs(run);
  if (Number.isFinite(scanStartMs)) {
    return Math.max(0, Math.floor((now - scanStartMs) / 1000));
  }
  return 0;
}

function _parseMs(value) {
  if (!value) return NaN;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
}

/**
 * Seconds spent waiting for a worker, separate from active scan time.
 *
 * Frozen once scanning begins (server-provided `queue_wait_seconds`); computed
 * live only while the job is still queued. `null` when there is no evidence.
 */
export function queueWaitSeconds(run, now = Date.now()) {
  const explicit = Number(run?.queue_wait_seconds);
  if (Number.isFinite(explicit) && explicit >= 0) return Math.floor(explicit);
  const queuedAt = new Date(run?.queued_at || run?.created_at || NaN).getTime();
  if (!Number.isFinite(queuedAt)) return null;
  const activeMs = activeStartMs(run);
  if (Number.isFinite(activeMs)) {
    return Math.max(0, Math.floor((activeMs - queuedAt) / 1000));
  }
  if (String(run?.status || "").toLowerCase() !== "queued") return null;
  return Math.max(0, Math.floor((now - queuedAt) / 1000));
}

/**
 * Explicit phase for the customer: queued / starting / running / waiting for
 * retailer / paused / completed / cancelled / failed.
 *
 * Prefers the server's phase (derived from persisted facts); the local fallback
 * exists only so an older payload still reads honestly.
 */
export function phasePresentation(run) {
  const server = run?.phase;
  if (server && typeof server === "object" && server.label) {
    return { key: String(server.key || ""), label: String(server.label), tone: _phaseTone(server.key) };
  }
  const status = String(run?.status || "queued").toLowerCase();
  if (status === "queued") return { key: "queued", label: "Queued — waiting for an available worker", tone: "neutral" };
  if (status === "paused") return { key: "paused", label: "Paused", tone: "neutral" };
  if (status === "cancelled") return { key: "cancelled", label: "Cancelled", tone: "gray" };
  if (status === "failed") return { key: "failed", label: "Failed", tone: "red" };
  if (status === "completed" || status === "partial") return { key: "completed", label: "Completed", tone: "green" };
  const collected = Number(run?.progress?.products_collected) || 0;
  const pages = Number(run?.progress?.listing_pages_scanned) || 0;
  if (!collected && !pages) return { key: "waiting_for_retailer", label: "Waiting for retailer response", tone: "green" };
  return { key: "running", label: "Running — acquiring retailer data", tone: "green" };
}

function _phaseTone(key) {
  const k = String(key || "").toLowerCase();
  if (["failed", "cancelled"].includes(k)) return "red";
  if (["paused", "stopped"].includes(k)) return "amber";
  if (k === "queued") return "neutral";
  return "green";
}

/** Remaining authorized seconds — always derived as authorized − consumed. */
export function remainingDurationSeconds(run, now = Date.now()) {
  const authorized = authorizedDurationSeconds(run);
  if (authorized == null) return null;
  const consumed = Math.min(authorized, consumedDurationSeconds(run, now));
  return Math.max(0, authorized - consumed);
}

/** Single canonical timer state for the scan header, bar and activity line. */
export function timerState(run, now = Date.now()) {
  const authorized = authorizedDurationSeconds(run);
  const consumedRaw = consumedDurationSeconds(run, now);
  const consumed = authorized != null ? Math.min(consumedRaw, authorized) : consumedRaw;
  const remaining = authorized == null ? null : Math.max(0, authorized - consumed);
  const paused = String(run?.status || "").toLowerCase() === "paused";
  const terminal = ["completed", "partial", "failed", "cancelled"].includes(
    String(run?.status || "").toLowerCase(),
  );
  const pct =
    authorized != null && authorized > 0
      ? Math.min(100, Math.max(0, (consumed / authorized) * 100))
      : null;
  return {
    authorized,
    consumed,
    remaining,
    paused,
    terminal,
    percent: pct,
  };
}
